import { Encryption, Storage, StorageConfig } from 'types/browser-storage';

import { parseToJSON } from 'utils/parse';
import { SessionStorageUtil } from 'utils/session-storage';

export class SessionStorage<T> extends Storage<T> {
  get config(): StorageConfig | null {
    const value = this.get();
    if (!value) return null;
    return value.config;
  }
  get value(): T | null {
    const value = this.get();
    if (!value) return null;
    return value?.data;
  }

  constructor(key: string, sensitive: boolean, version: number, encryption?: Encryption) {
    super(key, sensitive, version, encryption);
    this.clearOutDatedData();
  }

  get(): { data: T; config: StorageConfig } | null {
    if (this.sensitive && this.encryption) {
      const encrypted = SessionStorageUtil.get(this.key);
      if (encrypted === null) return null;
      const plainText = this.encryption.decrypt(encrypted, this.encryption.secretKey);
      return parseToJSON(plainText);
    } else {
      return SessionStorageUtil.getJSON(this.key);
    }
  }

  save(value: string) {
    let data = value;
    if (this.sensitive && this.encryption) {
      data = this.encryption.encrypt(data, this.encryption.secretKey);
    }
    SessionStorageUtil.set(this.key, data);
  }

  update(value: Partial<T>) {
    if (!this.config) return;
    const updated = { data: { ...this.value, ...value }, config: { ...this.config } };
    this.save(JSON.stringify(updated));
  }

  set(value: T) {
    const stored = { data: value, config: { version: this.version } };
    this.save(JSON.stringify(stored));
  }

  clear() {
    SessionStorageUtil.clear(this.key);
  }

  clearOutDatedData() {
    if (this.config?.version !== this.version) {
      this.clear();
    }
  }
}
